import { useEffect, useRef } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers';

import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import {
	usePathnameMatcher,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { useLoomCrossflowPrompt } from './useLoomCrossflowPrompt.ts';

export const useTWCG2110LoomCrossflow = (): void => {
	const isHome = usePathnameMatcher({ selector: (pathname) => pathname === '/wiki/home' });
	const prompt = useLoomCrossflowPrompt(isHome);
	const { isLoggedIn, isRovoLLMEnabled, accessStatus } = useSessionData();
	const isLicensed = useIsLicensed();
	const { openConversationAssistant } = useToggleConversationAssistant();
	const publish = usePublish('ai-mate');
	const { setQueryParams } = useRouteActions();
	const lastSentPrompt = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!prompt) {
			lastSentPrompt.current = undefined;
			return;
		}
		if (
			!isLicensed ||
			!isLoggedIn ||
			!isRovoLLMEnabled ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS ||
			lastSentPrompt.current === prompt
		)
			return;

		lastSentPrompt.current = prompt;
		openConversationAssistant('sidebar');
		publish({
			type: 'chat-new',
			consumeOnce: true,
			source: 'confluence-loom-crossflow',
			// Open directly so cold loads and an existing mini-modal do not depend on the navbar opener.
			openChat: false,
			openChatMode: 'sidebar',
			data: { dialogues: [], prompt },
		});
		setQueryParams({ loomCrossflowVideoId: null }, true);
	}, [
		prompt,
		isLicensed,
		isLoggedIn,
		isRovoLLMEnabled,
		accessStatus,
		openConversationAssistant,
		publish,
		setQueryParams,
	]);
};
